/***************
* *******
* *******
* Intervention Selector
* *******
* *******
*
* Given a State, chooses an Action
*
* In this context, "State" is defined as an object which contains all features necessary
* to choose an intervention.
*
* "Action" is the selected intervention. Action is a function of State, Configs, and Constants.
*
* interventionSelector exposes two things:

#1 //// selectIntervention(state)
- Given a state, chooses an intervention

#2 //// initializePassesWithCustomConfigs(CUSTOM_CONFIGS)
- A method to override default configs

****************/

//////
// Imports and initialization
import evalConfig from "./utils/evalConfig";
import { LOG_DECISION_CONTEXT } from "../config/";
import { interventionTypes } from "../constants";
import {
  checkForReadWithMeConditions,
  checkForReread,
  checkForTooManyInterventions,
} from "./edgeCases";
import getPassesForLocale from "./passes/getPassesForLocale";

//////
// Given a state, choose an intervention
export function selectIntervention(state) {
  let log = {
    locale: state.locale,
    selected: null,
    passSelected: null,
    word: state.word,
    wordIndex: state.wordIndex,
  };
  let intervention = null;
  let fallbackIntervention = null;
  let passSelected = null;
  let decisionContextAcrossPasses = []; // Initialize var to capture logging, toggled via LOG_DECISION_CONTEXT config

  // Always select comprehension intervention when question content exists, regardless of error count
  const { endOfPhrase, phraseMetaData, isValidRunForV2 } = state;

  const skipComprehensionOverrideForV2 = isValidRunForV2 && state.word;
  if (
    !skipComprehensionOverrideForV2 &&
    endOfPhrase &&
    phraseMetaData &&
    phraseMetaData.questions &&
    phraseMetaData.questions.length
  ) {
    intervention = interventionTypes.comprehension;
    LOG_DECISION_CONTEXT &&
      decisionContextAcrossPasses.push(
        "SELECTION_RULE - always choose comprehension if questions exist"
      );
  }

  // Handle edge cases
  if (!intervention) {
    intervention = checkForReadWithMeConditions(
      state,
      decisionContextAcrossPasses
    ); // Handle the "read with me" edge case
  }
  if (!intervention) {
    intervention = checkForReread(state, decisionContextAcrossPasses); // Handle "rereading phrase" edge case
  }
  if (!intervention) {
    intervention = checkForTooManyInterventions(
      state,
      decisionContextAcrossPasses
    );
  }

  // If no "edge cases" choose an intervention,
  // if a word has been selected for intervention, run the passes
  if (!intervention) {
    if (state.word) {
      let {
        selectedInterventionT,
        fallbackInterventionT,
        passSelected: selectedInPass,
        decisionContextAcrossPassesAcc,
        log: passLog,
      } = runPasses(state);
      intervention = selectedInterventionT;
      fallbackIntervention = fallbackInterventionT;
      passSelected = selectedInPass;
      log.passSelected = selectedInPass;
      decisionContextAcrossPasses = decisionContextAcrossPassesAcc;

      if (passLog) {
        log.passes = passLog;
      }
    } else {
      // otherwise, return a "none" selection
      intervention = interventionTypes.none;
      LOG_DECISION_CONTEXT &&
        decisionContextAcrossPasses.push(
          "NO_ERRORS_RULE - selected no intervention due to no word being chosen by intervene selector"
        );
    }
  }

  log.selected = intervention;

  // Return the results of the selection
  return {
    intervention,
    fallbackIntervention,
    passSelected,
    decisionContextAcrossPasses,
    log,
  };
}

//////
// Go through each pass until an intervention is selected
function runPasses(state) {
  let log = {};
  let selectedInterventionT = null;
  let fallbackInterventionT = null;
  let passSelected = null;
  let decisionContextAcrossPassesAcc = [];

  const { configId, disabledInterventionTypes, passes } = getPassesForLocale(
    state.locale
  );
  log.passesConfig = {
    configId,
    disabledInterventionTypes,
  };

  for (let i = 0; i < passes.length; i++) {
    // Check whether the pass selects an intervention
    let {
      selectedInterventionType,
      fallbackInterventionType,
      decisionContext,
      log: passLog,
    } = passes[i].run(state);
    log[passes[i].label] = passLog;
    decisionContextAcrossPassesAcc.push(decisionContext);

    if (selectedInterventionType) {
      // If an intervention has been selected
      selectedInterventionT = selectedInterventionType;
      if (fallbackInterventionType) {
        fallbackInterventionT = fallbackInterventionType;
      }
      passSelected = passes[i].label;
      break;
    } // Otherwise, move on to the next pass
  }
  if (!selectedInterventionT) {
    selectedInterventionT = interventionTypes.none;
    decisionContextAcrossPassesAcc.push(
      "SELECTION_RULE - Intervention didn't fire on any pass"
    );
    log.decision = `Intervention didn't fire on any pass`;
  }
  return {
    selectedInterventionT,
    fallbackInterventionT,
    passSelected,
    decisionContextAcrossPassesAcc,
    log,
  };
}

//////
// Allow the user to specify a set of custom configs.
/*
Note: These configs include functions specified as strings.
This is a potential security issue, and we need to think carefully about whether
we want the 'eval' function anywhere in our prod codebase.
*/
// export function initializeInterventionPassesWithCustomConfigs(CUSTOM_CONFIGS) {
//   if(ENABLE_EVAL_CONFIGS) {
//     CUSTOM_CONFIGS = evalConfig(CUSTOM_CONFIGS);
//   }
//   pass1 = new Pass1(CUSTOM_CONFIGS);
//   pass2 = new Pass2(CUSTOM_CONFIGS);
//   pass3 = new Pass3(CUSTOM_CONFIGS);
//   pass4 = new Pass4(CUSTOM_CONFIGS);
//   pass5 = new Pass5(CUSTOM_CONFIGS);
//   passes = [pass1,pass2,pass3, pass4, pass5];
// }
